/**
 * Skeleton studio contract (docs/SKEL-EDITOR.md §3): the SkelForm 0.8 `armature.json` types and
 * the Story Studio project envelope that wraps one armature per view.
 *
 * Conventions (SkelForm): world space is pixels of the source art, x right, **y up**; rotations
 * are radians, counter-clockwise positive; a bone's `pos` is local to its parent (after the
 * parent's rotation and scale). Texture atlas rects are y-down pixels. In a Story Studio project
 * every bone `id` equals its array index and every parent comes before its children
 * (`parent_id < id`), so one forward pass constructs the world pose.
 *
 * The runtime maths follow rusty_skelform 0.8 (MIT, © 2025 Retropaint), re-implemented clean-room;
 * the SkelForm editor itself (GPL-3.0) was studied for behaviour only.
 */

export interface V2 {
  x: number
  y: number
}

/** Multiplicative colour, each channel 0..1. */
export interface Tint {
  r: number
  g: number
  b: number
  a: number
}

export interface RGBA8 {
  r: number
  g: number
  b: number
  a: number
}

export interface SkfBone {
  id: number
  name: string
  /** −1 = root. Always emitted (the serde default is 0, not −1). */
  parent_id: number
  pos: V2
  scale: V2
  rot: number
  hidden?: boolean
  init_pos: V2
  init_rot: number
  init_scale: V2
  init_hidden?: boolean
  /** Index into `inverse_kinematics`, set on every bone of a chain; −1 = none. */
  ik_family_id: number
  ik_family_name?: string
  physics_id: number
  /** Index into `visuals`; −1 = an untextured joint/helper bone. */
  visuals_id: number
}

export interface SkfVertex {
  id: number
  pos: V2
  uv: V2
  init_pos: V2
}

export interface SkfBindVert {
  id: number
  weight: number
}

export interface SkfBind {
  bone_id: number
  is_path: boolean
  verts: SkfBindVert[]
}

export interface SkfVisuals {
  /** Texture NAME, resolved through the active styles in priority order. */
  tex: string
  tint?: Tint
  /** Draw order, ascending: a higher z-index is drawn on top. */
  zindex: number
  /** Bone origin → texture centre, in bone axes, divided by the texture size (w, h), y up. */
  pivot_pos: V2
  /** Extra texture rotation relative to the bone (radians). */
  pivot_rot: number
  pivot_scale: V2
  vertices?: SkfVertex[]
  indices?: number[]
  binds?: SkfBind[]
  init_tex: string
  init_tint?: Tint
  init_zindex: number
}

export type IkConstraint = 'None' | 'Clockwise' | 'CounterClockwise'
export type IkMode = 'FABRIK' | 'Arc'

export interface SkfIK {
  id: number
  constraint?: IkConstraint
  mode?: IkMode
  /** Bone index of the target; −1 = inactive chain. */
  target_id: number
  /** Chain bone indices, root first, end last. */
  bone_ids?: number[]
  mimic_target?: boolean
  init_constraint?: IkConstraint
  init_mode?: IkMode
  init_mimic_target?: boolean
}

/** Physics is preserved on round-trip but not simulated by this runtime (v1). */
export interface SkfPhysics {
  global_pos?: V2
  global_rot?: number
  global_scale?: V2
  pos_damping?: number
  scale_damping?: number
  rot_damping?: number
  pos_ratio?: number
  scale_ratio?: number
  global_orbit?: number
  global_orbit_diff?: number
  global_orbit_vel?: number
  sway?: number
  rot_bounce?: number
}

export interface SkfTexture {
  name: string
  /** Atlas rect top-left (y-down px). Inside a project (no atlas) this is {0,0}. */
  offset: V2
  /** Rect size in px = the quad size in world px at scale 1. */
  size: V2
  atlas_idx: number
}

export interface SkfStyle {
  id: number
  name: string
  textures: SkfTexture[]
}

export interface SkfAtlas {
  filename: string
  size: V2
}

export type AnimElement =
  | 'PositionX'
  | 'PositionY'
  | 'Rotation'
  | 'ScaleX'
  | 'ScaleY'
  | 'Zindex'
  | 'Texture'
  | 'IkConstraint'
  | 'Hidden'
  | 'IkMode'
  | 'IkFamilyId'
  | 'TintR'
  | 'TintG'
  | 'TintB'
  | 'TintA'
  | 'MimicTarget'

/** Elements whose value is interpolated; every other element steps (holds until the next key). */
export const INTERPOLATED_ELEMENTS: readonly AnimElement[] = [
  'PositionX',
  'PositionY',
  'Rotation',
  'ScaleX',
  'ScaleY',
  'TintR',
  'TintG',
  'TintB',
  'TintA',
] as const

export type HandlePreset = 'Linear' | 'SineIn' | 'SineOut' | 'SineInOut' | 'Snap' | 'Custom'

export interface SkfKeyframe {
  frame: number
  bone_id: number
  element: AnimElement
  value: number
  value_str?: string
  /** Bezier P1 of the curve ARRIVING at this key (handles live on the destination key). */
  start_handle: V2
  /** Bezier P2 of the curve arriving at this key. y = 999 on both = snap (hold). */
  end_handle: V2
  /** Index of the next key with the same bone and element, −1 if none. Always recomputed. */
  next_kf: number
  handle_preset?: HandlePreset | string
}

export interface SkfAnimation {
  name: string
  id: number
  fps: number
  keyframes: SkfKeyframe[]
}

export interface SkfRoot {
  version: string
  baked_ik: boolean
  img_format: 'PNG' | 'JPG'
  clear_color?: RGBA8
  bones: SkfBone[]
  animations?: SkfAnimation[]
  atlases: SkfAtlas[]
  styles: SkfStyle[]
  visuals: SkfVisuals[]
  inverse_kinematics: SkfIK[]
  physics: SkfPhysics[]
}

export const SKF_VERSION = '0.8.0'

export const HANDLES: Record<Exclude<HandlePreset, 'Custom'>, readonly [V2, V2]> = {
  Linear: [
    { x: 1 / 3, y: 1 / 3 },
    { x: 2 / 3, y: 2 / 3 },
  ],
  SineIn: [
    { x: 0.5, y: 0 },
    { x: 1, y: 1 },
  ],
  SineOut: [
    { x: 1 / 3, y: 1 },
    { x: 2 / 3, y: 1 },
  ],
  SineInOut: [
    { x: 0.5, y: 0 },
    { x: 0.5, y: 1 },
  ],
  Snap: [
    { x: 999, y: 999 },
    { x: 999, y: 999 },
  ],
}

// ---------------------------------------------------------------- Story Studio project envelope

export type ViewKind = 'front' | 'side' | 'custom'
export type Facing = 'camera' | 'right' | 'left'

export interface SkelCamera {
  /** World point (y-up px) at the centre of the canvas. */
  x: number
  y: number
  /** Screen px per world px. */
  zoom: number
}

export interface SkelView {
  kind: ViewKind
  facing: Facing
  root: SkfRoot
  /**
   * Content-addressed texture bytes: key `"<styleId>/<textureName>"` → 16-hex sha of the PNG,
   * served at `/api/skel/projects/<id>/tex/<sha>.png`.
   */
  tex_files: Record<string, string>
  camera?: SkelCamera | null
  /** Editor-only: style ids active in the editor, in priority order (default: all, file order). */
  active_styles?: number[] | null
}

/**
 * View kinds of auto-rigged puppets (docs/SKEL-STORY.md P3). A puppet's `SkelView.kind` can be any
 * `SkelViewKind`; `ViewKind` stays the editor's original three so exhaustive `Record<ViewKind, …>`
 * tables keep compiling.
 */
export type PuppetViewKind =
  | 'quadruped'
  | 'bird'
  | 'fish'
  | 'serpent'
  | 'insect'
  | 'radial'
  | 'blob'
  | 'plant'
  | 'prop'
export type SkelViewKind = ViewKind | PuppetViewKind

/** Story category of a puppet (§3.1 `meta.kind`). */
export type PuppetKind = 'person' | 'animal' | 'bird' | 'fish' | 'creature' | 'object'
/** Body plan the auto-rig chose (§3.1 `meta.plan`). */
export type PuppetPlan =
  | 'humanoid'
  | 'quadruped'
  | 'bird'
  | 'fish'
  | 'serpent'
  | 'insect'
  | 'radial'
  | 'blob'
  | 'plant'
  | 'prop'
/** A = full limbs, B = coarse / spine chain, C = one rigid bone; `sheet` / `manual` = not auto-rigged. */
export type PuppetTier = 'A' | 'B' | 'C' | 'sheet' | 'manual'

/** `SkelProjectBody.meta` (docs/SKEL-STORY.md §3.1). Every field is optional on read. */
export interface SkelPuppetMeta {
  /** Catalog link, e.g. `char:animals/forest/fox`; null for sheet / import / blank projects. */
  asset_uid?: string | null
  kind?: PuppetKind | null
  plan?: PuppetPlan | null
  tier?: PuppetTier | null
  display_name?: string | null
  /** One line, used in the story brief. */
  description?: string | null
  height_cm?: number | null
  /** TTS voice role key, or null for the default. */
  voice_role?: string | null
  speaks?: boolean | null
  /** Computed by the server on every save: idle, move and talk resolvable (P4). */
  story_ready?: boolean | null
  /** Set by the server after any save from the editor; the auto-rig never overwrites it silently. */
  human_edited?: boolean | null
  source_etag?: string | null
  pipeline_version?: number | null
  quality?: {
    rest_diff?: number
    rest_bad?: number
    gates?: { rest?: boolean; presets?: boolean; structure?: boolean }
    views?: Record<string, unknown>
  } | null
}

export interface SkelProjectBody {
  name: string
  view_order: string[]
  views: Record<string, SkelView>
  source?: { sheet_id: string } | null
  /** Puppet meta (§3.1). Omit it on save to keep the stored one; `null` removes it. */
  meta?: SkelPuppetMeta | null
}

export interface SkelProjectEnvelope {
  id: string
  rev: number
  created_at: string
  updated_at: string
  body: SkelProjectBody
}

export interface SkelProjectSummary {
  id: string
  name: string
  rev: number
  updated_at: string
  views: string[]
  anim_count: number
  thumb_url: string | null
  /** Puppet fields (§3.1); null / absent for projects without meta. */
  display_name?: string | null
  kind?: PuppetKind | null
  plan?: PuppetPlan | null
  tier?: PuppetTier | null
  story_ready?: boolean | null
  asset_uid?: string | null
}

export function texKey(styleId: number, texName: string): string {
  return `${styleId}/${texName}`
}
